import { computed, DestroyRef, effect, inject, Service, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { QrService } from '../../data/qr.service';
import { pick } from '../../data/qr.mapper';
import { QrTokenService } from '../../core/qr-token.service';
import { LanguageService } from '../../core/language.service';
import { TableSocketService } from '../../core/table-socket.service';
import { ToastService } from '../../shared/toast/toast.service';
import { CartStore } from './cart-store';
import { STORAGE_KEYS } from '../../core/constants';
import { TranslateService } from '@ngx-translate/core';
import { QrAddItemsRequest, QrOrder, TableEvent } from '../../data/table.types';

/** Per-order bill flow: idle → requested (REST accepted) → confirmed (BILL_CONFIRMED). */
export type BillState = 'idle' | 'requested' | 'confirmed';

/** call-waiter goes to the order's own waiter — an order without one has nobody to call. */
export function hasWaiter(order: QrOrder): boolean {
  return order.waiterId != null;
}

/** Waiter spam-guard window (backend enforces 30s; UI mirrors it to keep the button disabled). */
const WAITER_COOLDOWN_MS = 30_000;

/** Add-items spam-guard window. The backend enforces 10s PER TABLE — a neighbour's request can
 * occupy it — so this mirror only keeps our own button honest; it can't predict a 400. */
const ADD_ITEMS_COOLDOWN_MS = 10_000;

/** BILL_CONFIRMED comes from a real waiter and may never arrive (ignored push, station offline) —
 * after this window the optimistic 'requested' reverts to 'idle' as a retry affordance. The
 * backend re-allows a request after 30s, so a revert this late can't hit the spam guard. */
const BILL_CONFIRM_TIMEOUT_MS = 60_000;

/**
 * Confirmed bills survive a reload: the guest returns from the payment page in a fresh document,
 * and without this the block on further ordering would silently lift. Ids of orders the backend
 * no longer lists are dropped by pruneOrderStates, so this never accumulates.
 */
function readConfirmedBills(): Record<number, BillState> {
  try {
    if (typeof sessionStorage === 'undefined') return {};
    const raw = sessionStorage.getItem(STORAGE_KEYS.BILL_CONFIRMED);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return {};
    const states: Record<number, BillState> = {};
    for (const id of parsed) {
      if (typeof id === 'number' && Number.isFinite(id)) states[id] = 'confirmed';
    }
    return states;
  } catch {
    // sessionStorage unavailable (private mode / blocked) — the block lives for this document only.
    return {};
  }
}

/**
 * Holds the table's active orders and the call-waiter / request-bill / add-items / create-order
 * flows.
 * Guest actions go over REST; the socket only delivers confirmations (BILL_CONFIRMED,
 * QR_ITEMS_CONFIRMED, …) that update state and raise toasts. Page-scoped (provided in QrMenu).
 */
@Service({ autoProvided: false })
export class TableStore {
  private qr = inject(QrService);
  private qrToken = inject(QrTokenService);
  private socket = inject(TableSocketService);
  private cart = inject(CartStore);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private lang = inject(LanguageService).lang;

  readonly tableUuid = this.qrToken.tableUuid;

  private readonly _tableName = signal<string | null>(null);
  /** Human-readable table name (e.g. «Стол 7»); null until loaded or when no table was scanned. */
  readonly tableName = this._tableName.asReadonly();

  private readonly _orders = signal<QrOrder[]>([]);
  /**
   * Orders as the bill renders them: every backend-sent name resolved to the active language.
   * The raw payload stays in `_orders`, so a language switch relabels the bill without a refetch.
   */
  readonly orders = computed<QrOrder[]>(() => {
    const lang = this.lang();
    return this._orders().map((o) => ({
      ...o,
      items: (o.items ?? []).map((i) => ({
        ...i,
        name: pick(lang, i.name, i.nameRu),
        categoryName: pick(lang, i.categoryName, i.categoryNameRu),
        modificationGroups: i.modificationGroups?.map((g) => ({
          ...g,
          name: pick(lang, g.name, g.nameRu),
          modifications: (g.modifications ?? []).map((m) => ({
            ...m,
            name: pick(lang, m.name, m.nameRu),
          })),
        })),
      })),
    }));
  });
  private readonly _ordersLoading = signal(false);
  readonly ordersLoading = this._ordersLoading.asReadonly();
  private readonly _ordersError = signal(false);
  readonly ordersError = this._ordersError.asReadonly();

  private readonly _billStates = signal<Record<number, BillState>>(readConfirmedBills());
  readonly billStates = this._billStates.asReadonly();

  /** A confirmed bill closes ordering for the whole table — the guest is settling up. */
  readonly billConfirmed = computed(() =>
    Object.values(this._billStates()).some((state) => state === 'confirmed'),
  );

  /** The table has an active order the cart can append to. */
  readonly hasOrders = computed(() => this._orders().length > 0);

  /** Last bill-request failure (the same text this store toasts). The orders sheet mirrors it
   * into its in-modal live region — a toast under an aria-modal sheet is never announced. */
  private readonly _billError = signal<{ orderId: number; text: string } | null>(null);
  readonly billError = this._billError.asReadonly();

  /** Whether the LAST confirmed bill wiped a non-empty cart. The orders sheet appends it to its
   * own announcement — the toast host is inert under an open aria-modal sheet (WCAG 4.1.3). */
  private readonly _cartClearedOnBill = signal(false);
  readonly cartClearedOnBill = this._cartClearedOnBill.asReadonly();

  private readonly _waiterCooldown = signal(false);
  readonly waiterCooldown = this._waiterCooldown.asReadonly();
  /** The waiter tap is re-reading the table's orders before it decides whom to call. */
  private readonly _waiterChecking = signal(false);
  readonly waiterChecking = this._waiterChecking.asReadonly();

  private readonly _addItemsSending = signal(false);
  readonly addItemsSending = this._addItemsSending.asReadonly();
  private readonly _addItemsCooldown = signal(false);
  readonly addItemsCooldown = this._addItemsCooldown.asReadonly();
  /** Outcome of the last send ('sent' appended to an order, 'created' opened one), consumed (and
   * reset) by the cart sheet — see resetAddItemsResult. */
  private readonly _addItemsResult = signal<'idle' | 'sent' | 'created' | 'error'>('idle');
  readonly addItemsResult = this._addItemsResult.asReadonly();
  /** Text of the last failure — the same label this store toasts. The result signal alone can't
   * tell a rejection from an outage, and the cart sheet must repeat it in its in-modal region. */
  private readonly _addItemsError = signal('');
  readonly addItemsError = this._addItemsError.asReadonly();

  private waiterTimer: ReturnType<typeof setTimeout> | null = null;
  private addItemsTimer: ReturnType<typeof setTimeout> | null = null;
  /** Per-order revert timers for the optimistic 'requested' bill state. */
  private billTimers = new Map<number, ReturnType<typeof setTimeout>>();
  /** Per-order requestBill() attempt id — a hung POST can outlive the 60s revert and a re-tap,
   * and its late error must not touch the newer attempt's state (same idea as ordersReq). */
  private billReq = new Map<number, number>();
  /** Monotonic loadOrders() id — responses of superseded requests are dropped. */
  private ordersReq = 0;

  constructor() {
    this.socket.events$.pipe(takeUntilDestroyed()).subscribe((e) => this.onEvent(e));

    // Mirror the confirmed bills so a reload (or the return from the payment page) still knows
    // ordering is closed. Same write-through shape the cart uses.
    effect(() => {
      const ids = Object.entries(this._billStates())
        .filter(([, state]) => state === 'confirmed')
        .map(([id]) => Number(id));
      try {
        if (typeof sessionStorage === 'undefined') return;
        if (ids.length === 0) sessionStorage.removeItem(STORAGE_KEYS.BILL_CONFIRMED);
        else sessionStorage.setItem(STORAGE_KEYS.BILL_CONFIRMED, JSON.stringify(ids));
      } catch {
        // Storage unavailable — the block still holds for this document.
      }
    });

    // On reconnect (not the first connect) re-fetch orders to catch events missed while offline.
    let seenConnected = false;
    effect(() => {
      if (this.socket.connected()) {
        // untracked: loadOrders() reads tableUuid(); this effect must depend on connected() only.
        if (seenConnected) untracked(() => this.loadOrders());
        seenConnected = true;
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.waiterTimer) clearTimeout(this.waiterTimer);
      if (this.addItemsTimer) clearTimeout(this.addItemsTimer);
      for (const timer of this.billTimers.values()) clearTimeout(timer);
      this.billTimers.clear();
    });
  }

  /** Fetch the table's display name. Cosmetic — a failure just leaves the chip hidden. */
  loadTable(): void {
    const uuid = this.tableUuid();
    if (!uuid) return;
    this.qr
      .getTable(uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (t) => this._tableName.set(t.name),
        error: () => {},
      });
  }

  /** `then` gets this request's own result (null on failure) even when a newer request has
   * superseded it — a caller waiting on it must never be left hanging. */
  loadOrders(then?: (orders: QrOrder[] | null) => void): void {
    const uuid = this.tableUuid();
    if (!uuid) return;
    // Calls overlap (initial load, socket reconnects, retry) — only the latest request may
    // write state, so a slow stale response can't overwrite fresher orders.
    const req = ++this.ordersReq;
    this._ordersLoading.set(true);
    this._ordersError.set(false);
    this.qr
      .getTableOrders(uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (orders) => {
          if (req === this.ordersReq) {
            this._orders.set(orders);
            this._ordersLoading.set(false);
            this.pruneOrderStates(orders);
          }
          then?.(orders);
        },
        error: () => {
          if (req === this.ordersReq) {
            this._ordersLoading.set(false);
            this._ordersError.set(true);
          }
          then?.(null);
        },
      });
  }

  /**
   * The waiter tap. Nothing pushes a waiter-opened order (or a newly assigned waiter) to the
   * table topic, so the list is re-read first, then: no order / no order with a waiter → say
   * why; one order → call its waiter; several → `pick()` lets the guest choose (the sheet then
   * calls sendWaiterCall).
   */
  callWaiter(pick: () => void): void {
    if (!this.tableUuid() || this._waiterCooldown() || this._waiterChecking()) return;
    this._waiterChecking.set(true);
    this.loadOrders((orders) => {
      this._waiterChecking.set(false);
      if (!orders) this.toast.show(this.translate.instant('waiter.error'), 'error');
      else if (!orders.length) this.toast.show(this.translate.instant('waiter.noOrder'), 'info');
      else if (!orders.some(hasWaiter)) {
        this.toast.show(this.translate.instant('waiter.noWaiter'), 'info');
      } else if (orders.length === 1) this.sendWaiterCall(orders[0].id);
      else pick();
    });
  }

  sendWaiterCall(orderId: number): void {
    const uuid = this.tableUuid();
    if (!uuid || this._waiterCooldown()) return;

    this.startWaiterCooldown();
    this.qr
      .callWaiter(uuid, orderId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // The 200 itself means "call delivered" — no socket event follows (guide §2.2).
        next: () => this.toast.show(this.translate.instant('waiter.called'), 'success'),
        error: (err: HttpErrorResponse) => {
          if (err.status === 400) {
            // Spam guard: already called. Keep the cooldown, just inform the guest.
            this.toast.show(this.translate.instant('waiter.alreadyCalled'), 'info');
          } else {
            this.clearWaiterCooldown();
            this.toast.show(this.translate.instant('waiter.error'), 'error');
          }
        },
      });
  }

  /** Requests the bill over REST; the waiter's confirmation arrives later as BILL_CONFIRMED. */
  requestBill(orderId: number): void {
    const uuid = this.tableUuid();
    // Guard behind the sheet's aria-disabled (still clickable) button: the requested/confirmed
    // states must be a no-op, not an error toast.
    if (!uuid || (this._billStates()[orderId] ?? 'idle') !== 'idle') return;

    // Optimistic: the button flips to «запрошен…» right away; the error path rolls it back.
    this._billStates.update((s) => ({ ...s, [orderId]: 'requested' }));
    this.scheduleBillRevert(orderId);

    const req = (this.billReq.get(orderId) ?? 0) + 1;
    this.billReq.set(orderId, req);
    this.qr
      .requestBill(uuid, orderId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        // 200 = the waiter got the push; 'requested' is already showing and only
        // BILL_CONFIRMED moves it further.
        next: () => {},
        error: (err: HttpErrorResponse) => {
          if (this.billReq.get(orderId) !== req) return;
          // Only a still-'requested' order gets the rollback and the failure surfacing: a
          // BILL_CONFIRMED that raced this failing POST must not be shouted over, and after
          // the 60s revert there is nothing left to roll back or report.
          if ((this._billStates()[orderId] ?? 'idle') === 'requested') {
            // Roll back on EVERY failure, the spam-guard 400 included: that guard is per
            // TABLE, so the 400 may belong to a tablemate's request for another order —
            // keeping 'requested' would show a wait no BILL_CONFIRMED will ever resolve.
            this.clearBillRevert(orderId);
            this._billStates.update((s) => ({ ...s, [orderId]: 'idle' }));
            const text = this.translate.instant(
              err.status === 400 ? 'bill.alreadyRequested' : 'bill.requestError',
            );
            this._billError.set({ orderId, text });
            this.toast.show(text, err.status === 400 ? 'info' : 'error');
          }
          // 404 — the order closed or was paid meanwhile; refetch so the sheet catches up.
          if (err.status === 404) this.loadOrders();
        },
      });
  }

  /** Appends cart lines to an active order. Outcome is surfaced through the addItems* signals. */
  addItemsToOrder(orderId: number, items: QrAddItemsRequest['items']): void {
    this.send(items, (uuid) => this.qr.addOrderItems(uuid, orderId, { items }), 'sent');
  }

  /** Opens a new order from the cart on a table that has none. Same signals as addItemsToOrder. */
  createOrder(items: QrAddItemsRequest['items']): void {
    // Nothing beyond the lines is asked of the guest: one guest, no order-level note — the waiter
    // sets the real head count when approving.
    this.send(
      items,
      (uuid) => this.qr.createOrder(uuid, { items, comment: '', guestCount: 1 }),
      'created',
    );
  }

  /** Shared add-items / create-order pipeline: same guards, cooldown and result signals — the
   * backend's 10s table window covers both calls alike. */
  private send(
    items: QrAddItemsRequest['items'],
    request: (uuid: string) => Observable<QrOrder>,
    outcome: 'sent' | 'created',
  ): void {
    const uuid = this.tableUuid();
    if (!uuid || this._addItemsSending() || this._addItemsCooldown() || items.length < 1) return;
    if (items.length > 30) {
      // Unlike the guards above this one is reachable with an enabled button — say why, or the
      // button just looks dead.
      this.toast.show(this.translate.instant('cart.max30'), 'info');
      return;
    }

    this._addItemsSending.set(true);
    this._addItemsError.set('');
    request(uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.applyUpdatedOrder(updated);
          this.startAddItemsCooldown();
          this._addItemsSending.set(false);
          this._addItemsResult.set(outcome);
          // Deliberately no success toast here: the cart sheet is still up (aria-modal) when this
          // runs, and a live region mutated underneath an open modal is not announced — nor
          // replayed once the modal goes. The sheet raises the outcome after it has closed.
        },
        // The cooldown starts on every outcome, errors included: the backend's 10s table window
        // may already be running (we can't tell from here), and a retry storm helps nobody.
        error: (err: HttpErrorResponse) => {
          this.startAddItemsCooldown();
          this._addItemsSending.set(false);
          this._addItemsResult.set('error');
          if (err.status === 400) {
            // 400 is validation / a vanished position just as much as the cooldown — a neutral
            // "try again in a couple of seconds" covers both without ever blaming the guest.
            const rejected = this.translate.instant('cart.retryInfo');
            this._addItemsError.set(rejected);
            this.toast.show(rejected, 'info');
          } else {
            const failed = this.translate.instant('cart.sendError');
            this._addItemsError.set(failed);
            this.toast.show(failed, 'error');
            // 404 means the order closed (or was paid), or on create the table is unknown —
            // refetch so the cart gating catches up.
            if (err.status === 404) this.loadOrders();
          }
        },
      });
  }

  /** The cart sheet reacts to `addItemsResult` in an effect; it resets here once handled, so a
   * stale 'sent' can't close a freshly opened sheet. */
  resetAddItemsResult(): void {
    this._addItemsResult.set('idle');
  }

  /**
   * Success path of send(). Bumping `ordersReq` FIRST invalidates any GET /orders still in
   * flight: one that started before the POST would land after this patch and silently wipe the
   * fresh PENDING_APPROVAL items. A known order is replaced, a new one (createOrder) appended;
   * a DTO that comes back trimmed falls back to a refetch (the orders sheet renders the money
   * fields).
   */
  private applyUpdatedOrder(updated: QrOrder): void {
    this.ordersReq++;
    if (typeof updated.totalPrice !== 'number') {
      this.loadOrders();
      return;
    }
    this._orders.update((orders) =>
      orders.some((o) => o.id === updated.id)
        ? orders.map((o) => (o.id === updated.id ? updated : o))
        : [...orders, updated],
    );
    // An invalidated GET will never clear the flag itself — its handler now bails on the req check.
    this._ordersLoading.set(false);
  }

  private onEvent(e: TableEvent): void {
    switch (e.event) {
      case 'BILL_CONFIRMED': {
        // parse() only validates `event` — check the payload shape before trusting it.
        const orderId = (e.data as { orderId?: unknown } | null)?.orderId;
        if (typeof orderId !== 'number') break;
        this.clearBillRevert(orderId);
        this._billStates.update((s) => ({ ...s, [orderId]: 'confirmed' }));
        // Ordering is over for this table — a cart left standing could only produce items the
        // waiter has already billed. The wipe rides the bill's own toast: a second one would
        // stack on the first and still be inert under an open sheet.
        const cleared = !this.cart.isEmpty();
        this._cartClearedOnBill.set(cleared);
        if (cleared) this.cart.clear();
        const coming = this.translate.instant('bill.coming');
        this.toast.show(
          cleared ? coming + '. ' + this.translate.instant('cart.clearedOnBill') : coming,
          'success',
        );
        break;
      }
      case 'QR_ITEMS_CONFIRMED':
        // The waiter approved the pending items — they left PENDING_APPROVAL and now count.
        this.loadOrders();
        this.toast.show(this.translate.instant('bill.itemsConfirmed'), 'success');
        break;
      // Order-affecting event — re-fetch to keep cart gating and bill sums current.
      // Deliberately NOT the default: TABLE_MESSAGE and unknown events must not each
      // cost a GET /orders; the sheet re-fetches on open as the catch-all.
      case 'ORDER_STATUS':
        this.loadOrders();
        break;
      default:
        break;
    }
  }

  /** Drops bill states of orders the backend no longer lists (paid, closed, merged). */
  private pruneOrderStates(orders: QrOrder[]): void {
    const active = new Set(orders.map((o) => o.id));
    this._billStates.update((states) => {
      const next: Record<number, BillState> = {};
      for (const [id, state] of Object.entries(states)) {
        if (active.has(Number(id))) next[Number(id)] = state;
      }
      return next;
    });
    // Dropping the attempt id also mutes a late error of a POST for a vanished order.
    for (const id of [...this.billReq.keys()]) {
      if (!active.has(id)) this.billReq.delete(id);
    }
  }

  private scheduleBillRevert(orderId: number): void {
    this.clearBillRevert(orderId);
    this.billTimers.set(
      orderId,
      setTimeout(() => {
        this.billTimers.delete(orderId);
        this._billStates.update((s) =>
          s[orderId] === 'requested' ? { ...s, [orderId]: 'idle' } : s,
        );
      }, BILL_CONFIRM_TIMEOUT_MS),
    );
  }

  private clearBillRevert(orderId: number): void {
    const timer = this.billTimers.get(orderId);
    if (timer !== undefined) {
      clearTimeout(timer);
      this.billTimers.delete(orderId);
    }
  }

  private startAddItemsCooldown(): void {
    this._addItemsCooldown.set(true);
    if (this.addItemsTimer) clearTimeout(this.addItemsTimer);
    this.addItemsTimer = setTimeout(() => this._addItemsCooldown.set(false), ADD_ITEMS_COOLDOWN_MS);
  }

  private startWaiterCooldown(): void {
    this._waiterCooldown.set(true);
    if (this.waiterTimer) clearTimeout(this.waiterTimer);
    this.waiterTimer = setTimeout(() => this._waiterCooldown.set(false), WAITER_COOLDOWN_MS);
  }

  private clearWaiterCooldown(): void {
    this._waiterCooldown.set(false);
    if (this.waiterTimer) {
      clearTimeout(this.waiterTimer);
      this.waiterTimer = null;
    }
  }
}
